import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';

import { api } from '../../../lib/api.js';
import { WorkspaceDetail, UsageResponse } from '@raven/shared-types';

function fmt(n: number): string {
  return new Intl.NumberFormat().format(n);
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

export function WorkspaceOverview() {
  const { id } = useParams({ strict: false }) as { id: string };
  const detail = useQuery({
    queryKey: ['workspace', id],
    queryFn: () => api.get(`/api/workspaces/${encodeURIComponent(id)}`, WorkspaceDetail),
  });
  const usage = useQuery({
    queryKey: ['usage', id],
    queryFn: () =>
      api.get(`/api/workspaces/${encodeURIComponent(id)}/usage`, UsageResponse),
  });

  if (detail.isPending) return <div className="card text-sm text-slate-500">Loading…</div>;
  if (detail.error) return <div className="card text-sm text-red-600">{String(detail.error)}</div>;
  const ws = detail.data!;

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{ws.name ?? ws.client_conf_key}</h2>
            <p className="mt-1 text-sm text-slate-600">{ws.domain ?? '—'}</p>
            <div className="mt-3 flex gap-2">
              {ws.modules.map((m) => (
                <span key={m} className="badge bg-brand-50 text-brand-700">
                  {m}
                </span>
              ))}
              {ws.is_suspended && <span className="badge-error">suspended</span>}
              {!ws.is_active && <span className="badge-warning">inactive</span>}
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600">
            <dt>POC</dt>
            <dd className="font-medium">{ws.poc_email ?? '—'}</dd>
            <dt>Reseller</dt>
            <dd className="font-medium">{ws.reseller_id ?? '—'}</dd>
            <dt>Onboarded</dt>
            <dd className="font-medium">{ws.onBoardedOn ? String(ws.onBoardedOn) : '—'}</dd>
          </dl>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Total hits" value={usage.data ? fmt(usage.data.totals.totalHits) : '—'} />
        <Kpi
          label="Cache hit ratio"
          value={usage.data ? pct(usage.data.totals.cache_hit_ratio) : '—'}
        />
        <Kpi
          label="Total renders"
          value={usage.data ? fmt(usage.data.totals.totalRendered) : '—'}
        />
        <Kpi label="Overage renders" value={usage.data ? fmt(usage.data.totals.extraRenders) : '—'} />
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="card">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
    </div>
  );
}
